import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownPopover,
  DropdownMenu,
  DropdownItem
} from '@heroui/react';
import { Icon } from '@iconify/react';

import {
  useBaseOwnerId,
  useModel,
  useModelDelete,
  useModelInvalidateIndex
} from '@rhino-project/core/hooks';
import { networkApiCall } from '@rhino-project/core/lib';
import {
  ModelIndexSimple,
  ModelIndexTable,
  ModelSearch,
  ModelFilters,
  ModelPager,
  ModelIndexActionCreateModal,
  ModelCreateModalActionSave,
  ModelPage
} from '@rhino-project/ui-heroui';

type LineupRow = {
  original: {
    id: number | string;
    name: string;
  };
};

type LineupRowActionsProps = {
  // injected by ModelIndexTable via cloneElement
  row?: LineupRow;
  id?: string;
  header?: string;
  onDuplicate: (id: number | string) => void;
  onDelete: (id: number | string, name: string) => void;
};

const LineupRowActions = ({
  row,
  onDuplicate,
  onDelete
}: LineupRowActionsProps) => {
  const { t } = useTranslation();

  if (!row) return null;
  const { id, name } = row.original;
  return (
    <Dropdown>
      <DropdownTrigger className="button--ghost min-w-0 px-1 py-0 h-auto">
        <Icon icon="bi:three-dots-vertical" width="18" height="18" />
      </DropdownTrigger>
      <DropdownPopover>
        <DropdownMenu aria-label={t('frontend.messages.lineup_actions')}>
          <DropdownItem
            key="duplicate"
            id="duplicate"
            textValue={t('frontend.messages.duplicate')}
            onAction={() => onDuplicate(id)}
          >
            {t('frontend.messages.duplicate')}
          </DropdownItem>
          <DropdownItem
            key="delete"
            id="delete"
            textValue={t('frontend.messages.delete')}
            onAction={() => onDelete(id, name)}
            className="text-danger"
          >
            {t('frontend.messages.delete')}
          </DropdownItem>
        </DropdownMenu>
      </DropdownPopover>
    </Dropdown>
  );
};

const LineupsPage = () => {
  const { t } = useTranslation();

  const { team_season_id: teamSeasonId } = useParams({ strict: false });
  const navigate = useNavigate();
  const baseOwnerId = useBaseOwnerId();
  const queryClient = useQueryClient();

  const lineupModel = useModel('lineup');
  const { invalidate: invalidateLineupsIndex } =
    useModelInvalidateIndex('lineup');
  const { mutate: deleteLineup, isPending: isDeleting } =
    useModelDelete(lineupModel);
  const [pendingDelete, setPendingDelete] = useState<{
    id: number | string;
    name: string;
  } | null>(null);

  const { mutate: duplicateLineup } = useMutation({
    mutationFn: (sourceId: number | string) =>
      networkApiCall(`${lineupModel.path}/${sourceId}/duplicate`, {
        method: 'post'
      })
  });

  const defaultFilter = useMemo(
    () => ({ team_season: teamSeasonId, kind: 'event' }),
    [teamSeasonId]
  );

  const parent = useMemo(() => ({ id: teamSeasonId }), [teamSeasonId]);

  const handleSave = useCallback(
    ({ id }: { id?: string | number }) => {
      if (id) navigate({ to: `./${id}` });
    },
    [navigate]
  );

  const createOverrides = useMemo(
    () => ({
      ModelCreateModalActionSave: {
        component: ModelCreateModalActionSave,
        props: { onSave: handleSave }
      }
    }),
    [handleSave]
  );

  const handleDuplicate = useCallback(
    (id: number | string) => {
      duplicateLineup(id, {
        onSuccess: (response) => {
          const data = (response as { data?: { id?: string | number } })?.data;
          const newId = data?.id;
          if (!newId) return;
          navigate({
            to: `/${baseOwnerId}/active/${teamSeasonId}/lineups/${newId}`
          });
        }
      });
    },
    [duplicateLineup, navigate, baseOwnerId, teamSeasonId]
  );

  const handleDelete = useCallback(
    (id: number | string, name: string) => setPendingDelete({ id, name }),
    []
  );

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    deleteLineup(pendingDelete.id as string, {
      onSuccess: () => {
        setPendingDelete(null);
        invalidateLineupsIndex();
        queryClient.invalidateQueries();
      }
    });
  }, [pendingDelete, deleteLineup, invalidateLineupsIndex, queryClient]);

  const tablePaths = useMemo(
    () => [
      'event_at',
      'name',
      // eslint-disable-next-line @eslint-react/no-missing-key
      <LineupRowActions
        id="actions"
        header=""
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
      />
    ],
    [handleDuplicate, handleDelete]
  );

  return (
    <ModelPage>
      <ModelIndexSimple
        analyticsSurface="index_page"
        model="lineup"
        defaultFilter={defaultFilter}
        defaultOrder={['-event_at']}
      >
        <div className="flex flex-col gap-3">
          <ModelSearch />
          <ModelFilters paths={[]} />
          <div className="flex flex-row">
            <div className="ml-auto">
              <ModelPager />
            </div>
          </div>
          <hr />
          <div className="flex flex-row flex-wrap justify-between">
            <ModelIndexActionCreateModal
              parent={parent}
              overrides={createOverrides}
            />
          </div>
          <ModelIndexTable paths={tablePaths} />
        </div>
      </ModelIndexSimple>
      <AlertDialog
        isOpen={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialog.Backdrop>
          <AlertDialog.Container size="sm">
            <AlertDialog.Dialog>
              <AlertDialog.CloseTrigger />
              <AlertDialog.Header>
                <AlertDialog.Icon status="danger" />
                <AlertDialog.Heading>
                  {t('frontend.messages.delete_lineup')}
                </AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                {t('frontend.messages.delete_confirmation', {
                  name: pendingDelete?.name
                })}
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="ghost" onPress={() => setPendingDelete(null)}>
                  {t('frontend.messages.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onPress={confirmDelete}
                  isDisabled={isDeleting}
                >
                  {isDeleting
                    ? t('frontend.messages.deleting')
                    : t('frontend.messages.delete_lineup')}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </ModelPage>
  );
};

export default LineupsPage;
